import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import type { ExtractRequest, ExtractResponse, ExtractionResult } from '../lib/types';

const callExtract = httpsCallable<ExtractRequest, ExtractResponse>(functions, 'extract');

export class ExtractionError extends Error {
  constructor(message: string, readonly retryable: boolean) {
    super(message);
    this.name = 'ExtractionError';
  }
}

export async function runExtraction(req: ExtractRequest): Promise<ExtractionResult> {
  let res;
  try {
    res = await callExtract(req);
  } catch (e) {
    const code = (e as { code?: string })?.code ?? '';
    const retryable =
      code.includes('unavailable') || code.includes('deadline-exceeded') || code.includes('internal');
    throw new ExtractionError(
      retryable
        ? 'Extraction service unreachable. The capture is saved — retry from the history list.'
        : ((e as Error)?.message ?? 'Extraction failed.'),
      retryable,
    );
  }

  const data = res.data;
  if (!data?.ok) {
    throw new ExtractionError(data?.error ?? 'Extraction returned no data.', false);
  }
  return data.data;
}

const callTranscribe = httpsCallable<
  { projectId: string; storagePath: string; mimeType: string },
  { ok: true; transcript: string; confidence: number | null } | { ok: false; error: string }
>(functions, 'transcribe');

/**
 * Turn a finished recording into words.
 *
 * Deliberately separate from extraction: transcription is about hearing, and
 * extraction is about what the words mean. Keeping them apart means a poor
 * transcript can be corrected by hand before any number is derived from it.
 */
export async function transcribeRecording(
  projectId: string,
  storagePath: string,
  mimeType: string,
): Promise<string> {
  let res;
  try {
    res = await callTranscribe({ projectId, storagePath, mimeType });
  } catch (e) {
    const code = (e as { code?: string })?.code ?? '';
    throw new ExtractionError(
      code.includes('unavailable') || code.includes('deadline-exceeded')
        ? 'Could not reach the transcriber. The recording is kept — try again.'
        : ((e as Error)?.message ?? 'Transcription failed.'),
      true,
    );
  }
  if (!res.data?.ok) {
    throw new ExtractionError(
      (res.data as { error?: string })?.error ?? 'Nothing could be made out.',
      false,
    );
  }
  return res.data.transcript;
}
